import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';
import TranscriptionDisplay from './TranscriptionDisplay';
import { getMediaUrl } from '../utils/mediaUrl';

const AudioPlayer = ({ audioUrl, isPlaying, onPlayStateChange, transcription, transcriptionWords }) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  
  // Fix audio URL to use backend URL if relative
  const getAudioUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    
    // If URL already starts with /api, use getMediaUrl directly
    if (url.startsWith('/api')) {
      return getMediaUrl(url);
    }
    
    // Otherwise, add /api prefix for Kubernetes ingress routing
    return getMediaUrl('/api' + url);
  };
  
  const fullAudioUrl = getAudioUrl(audioUrl);

  // Reset audio state when URL changes
  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
    setLoading(true);
    
    // Notify parent that playback stopped when audio URL changes
    if (isPlaying) {
      onPlayStateChange(false);
    }
  }, [audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setLoading(false);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      onPlayStateChange(false);
      setCurrentTime(0);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [onPlayStateChange]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      // Reset audio position if it's at the end
      if (audio.ended) {
        audio.currentTime = 0;
      }
      audio.play().catch(err => {
        console.error('Play error:', err);
        // Si la lecture échoue, notifier le parent
        onPlayStateChange(false);
      });
    } else {
      audio.pause();
    }
  }, [isPlaying, onPlayStateChange]);

  const togglePlay = () => {
    onPlayStateChange(!isPlaying);
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="w-full space-y-3" data-testid="audio-player">
      <audio
        ref={audioRef}
        src={fullAudioUrl}
        preload="metadata"
      />

      {/* Transcription Display (Karaoke) */}
      {transcriptionWords && transcriptionWords.length > 0 && (
        <TranscriptionDisplay
          words={transcriptionWords}
          currentTime={currentTime}
          isPlaying={isPlaying}
        />
      )}

      <div className="flex items-center space-x-4">
        {/* Play/Pause Button */}
        <button
          data-testid="audio-play-pause-btn"
          onClick={togglePlay}
          disabled={loading}
          className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-magenta-500 to-violet-600 rounded-full hover:from-magenta-600 hover:to-violet-700 transition-all disabled:opacity-50 flex-shrink-0"
        >
          {isPlaying ? (
            <Pause className="w-5 h-5" fill="white" />
          ) : (
            <Play className="w-5 h-5" fill="white" />
          )}
        </button>

        {/* Progress Bar */}
        <div className="flex-1">
          <div
            onClick={handleSeek}
            className="h-2 bg-white/10 rounded-full cursor-pointer relative group"
          >
            <div
              className="h-full bg-gradient-to-r from-magenta-500 to-violet-600 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ left: `${progress}%`, transform: 'translate(-50%, -50%)' }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
